// Archived bank accounts (CAS-1082) for the demo. Archiving hides an account
// from every overview, chart and total; it stays visible on the Accounts page
// and its detail. Restoring goes through the bank reconnect flow. State lives
// in sessionStorage so "Reset demo state" brings every account back.

const STORAGE_KEY = "fidoo-demo-archived-accounts-v1";

/** accountId → ISO time it was archived. */
type ArchivedMap = Record<string, string>;

function read(): ArchivedMap {
  try {
    return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) ?? "{}") as ArchivedMap;
  } catch {
    return {};
  }
}

function write(map: ArchivedMap) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Private mode — archiving just won't survive a reload.
  }
}

export function isArchivedAccountId(accountId: string): boolean {
  return accountId in read();
}

export function archivedAt(accountId: string): string | undefined {
  return read()[accountId];
}

export function archiveAccounts(accountIds: string[]) {
  const map = read();
  const now = new Date().toISOString();
  for (const id of accountIds) map[id] ??= now;
  write(map);
}

export function unarchiveAccounts(accountIds: string[]) {
  const map = read();
  for (const id of accountIds) delete map[id];
  write(map);
}

/** Pending reconnects: connectionId → the account being restored/refreshed. */
const RECONNECT_KEY = "fidoo-demo-reconnects-v1";

export function rememberReconnect(connectionId: string, accountId: string) {
  try {
    const all = JSON.parse(window.sessionStorage.getItem(RECONNECT_KEY) ?? "{}") as Record<string, string>;
    all[connectionId] = accountId;
    window.sessionStorage.setItem(RECONNECT_KEY, JSON.stringify(all));
  } catch {
    // ignore
  }
}

export function reconnectTarget(connectionId: string): string | undefined {
  try {
    return (JSON.parse(window.sessionStorage.getItem(RECONNECT_KEY) ?? "{}") as Record<string, string>)[
      connectionId
    ];
  } catch {
    return undefined;
  }
}

/** Accounts whose expired connection was refreshed via reconnect this session. */
const REFRESHED_KEY = "fidoo-demo-refreshed-connections-v1";

export function markConnectionsRefreshed(accountIds: string[]) {
  try {
    const all = new Set(
      JSON.parse(window.sessionStorage.getItem(REFRESHED_KEY) ?? "[]") as string[],
    );
    for (const id of accountIds) all.add(id);
    window.sessionStorage.setItem(REFRESHED_KEY, JSON.stringify([...all]));
  } catch {
    // ignore
  }
}

export function isConnectionRefreshed(accountId: string): boolean {
  try {
    return (
      JSON.parse(window.sessionStorage.getItem(REFRESHED_KEY) ?? "[]") as string[]
    ).includes(accountId);
  } catch {
    return false;
  }
}
